import type { AdapterContext } from "../base.js";
import type { ChapterRef } from "../../data-model/novel.js";
import type { ChapterIndex } from "../../data-model/primitives.js";
import { unsafeBrandChapterIndex } from "../../data-model/primitives.js";
import { CancelledError } from "../../core/errors.js";

export interface TocWalkPage {
  readonly refs: readonly ChapterRef[];
  /** Stop after yielding this page's refs. Omitted = keep walking. */
  readonly isLast?: boolean;
}

/**
 * Owns the cross-page invariants every paginated TOC walk shares: checks
 * ctx.signal before each page (throws CancelledError), yields refs in order,
 * advances startIndex by refs.length between pages, and stops on fetchPage
 * returning null, an empty refs array, or isLast. Pages are numbered from 1.
 */
export async function* paginateToc(opts: {
  readonly ctx: AdapterContext;
  readonly fetchPage: (page: number, startIndex: ChapterIndex) => Promise<TocWalkPage | null>;
}): AsyncIterable<ChapterRef> {
  let index = unsafeBrandChapterIndex(0);
  for (let page = 1; ; page++) {
    if (opts.ctx.signal.aborted) throw new CancelledError({ cause: opts.ctx.signal.reason });
    const result = await opts.fetchPage(page, index);
    if (result === null || result.refs.length === 0) return;
    for (const ref of result.refs) yield ref;
    index = unsafeBrandChapterIndex(index + result.refs.length);
    if (result.isLast) return;
  }
}
